/**
 * A LENNAR.COM CARD PHOTO, SWAPPED FOR ITS BYNDER ORIGINAL SO IT CAN BE CROPPED (2026-09-25)
 *
 * Jack, after the prod drive: card photos ran 100, 151 and 180 px tall in one email.
 * `cdn.lennar.com` cannot crop (its resize parameters 404 on some assets, measured 09-14),
 * so since #297 a feed photo keeps its own shape. But lennar.com's renders are uploaded
 * from Bynder and keep Bynder's file name, lowercased, often with a date suffix:
 *
 *   cdn.lennar.com/…/jax_40s50s_silvermeadows_rend_marsala_3840_marsala.jpg
 *   Bynder: JAX_40s50s_SilverMeadows_Rend_Marsala_3840_Marsala
 *
 * Measured for JAX (2026-09-25, 239 card photos): 81% of floorplan photos and 38% of
 * homesite photos have a Bynder copy found this way. Bynder's transform crops to the
 * card's exact frame (`fill,width:270,height:180`), which is what a Bynder pick already
 * does. So the fill asks Bynder once per photo and, on a match, uses the cropped Bynder
 * URL instead. No match → the lennar.com photo stands (and is cropped at submit, see
 * cropCardPhotos.ts).
 *
 * The lookup is async and the fill is not: this is a module store with a version, the
 * same pattern as the amenity icon library (echAmen `amenFolderVersion`). A landed lookup
 * bumps the version, `useBynderMatchVersion` re-renders its subscribers, and the fill
 * effect (useEchDocFill) re-runs with the answer in hand.
 */
import { useSyncExternalStore } from "react";
import { bynderSearch, type BynderAsset } from "./api";

type Answer = string | null;     /* Bynder transform BASE, or null = no match */
const store = new Map<string, Answer>();
const inflight = new Set<string>();
const queue: string[] = [];
let running = 0;
let version = 0;
const listeners = new Set<() => void>();
const bump = () => { version++; listeners.forEach((f) => { try { f(); } catch { /* a listener must not stop the rest */ } }); };

/* lookups are off under jest unless a suite turns them on — hundreds of fill tests would
   otherwise fire /bynder-search at a fetch double that fails on unexpected URLs */
let enabled = typeof process === "undefined" || !process.env || process.env.NODE_ENV !== "test";
export function __setBynderMatchEnabled(on: boolean): void { enabled = on; }
export function __resetBynderMatch(): void { store.clear(); inflight.clear(); queue.length = 0; running = 0; version = 0; }

/** only lennar.com's image CDN — anything else (Bynder, SFMC, a paste) is left alone */
export const isCdnPhoto = (u: unknown): boolean => /^https?:\/\/cdn\.lennar\.com\/api\/images\//i.test(String(u || "").trim());

/** the photo's identity: the file name, lowercased, without extension or query */
export function cdnStem(u: unknown): string {
  try {
    const f = decodeURIComponent(String(u || "").split("?")[0].split("#")[0].split("/").pop() || "");
    return f.replace(/\.(jpe?g|png|webp|gif)$/i, "").toLowerCase();
  } catch { return ""; }
}
/** the stem without the upload-date suffix and the rendition tails lennar.com adds */
export function trimStem(stem: string): string {
  return String(stem || "")
    .replace(/(_\d{1,2}_\d{1,2}_\d{2,4})+$/, "")
    .replace(/_(base\d+|planhero|hero|c)$/, "")
    .replace(/_f\d$/, "");
}

/** which of Bynder's answers is this photo — exact name, then the trimmed name, then a long prefix */
export function pickMatch(stem: string, items: BynderAsset[]): BynderAsset | null {
  const t = trimStem(stem);
  const nm = (x: BynderAsset) => String(x.name || "").toLowerCase();
  const withT = (items || []).filter((x) => transformOf(x));
  return withT.find((x) => nm(x) === stem)
    || withT.find((x) => trimStem(nm(x)) === t)
    || (t.length > 14 ? withT.find((x) => nm(x).startsWith(t)) : undefined)
    || null;
}
function transformOf(x: BynderAsset): string {
  const th = (x && x.thumbnails) || {};
  return String((x && x.transform) || th.transformBaseUrl || (th as Record<string, string>).transformbaseurl || "").trim();
}

function settle(stem: string): void { inflight.delete(stem); running--; bump(); pump(); }
function lookup(stem: string): void {
  running++;
  bynderSearch({ q: trimStem(stem), limit: 15, page: 1 })
    .then((r) => { const m = pickMatch(stem, (r && r.items) || []); store.set(stem, m ? transformOf(m) : null); })
    .catch(() => { store.set(stem, null); })
    .finally(() => settle(stem));
}
function pump(): void {
  while (running < 3 && queue.length) lookup(queue.shift()!);
}

/** ask Bynder about this photo once; the answer arrives through the version */
export function requestBynderMatch(u: unknown): void {
  if (!enabled || !isCdnPhoto(u)) return;
  const stem = cdnStem(u);
  if (!stem || store.has(stem) || inflight.has(stem)) return;
  inflight.add(stem);
  queue.push(stem);
  pump();
}

/**
 * The cropped Bynder URL for this lennar.com photo, or "" when there is none (yet).
 * Sync — the fill calls it; a miss starts the lookup.
 */
export function bynderCardUrl(u: unknown, w: number, h: number): string {
  if (!isCdnPhoto(u)) return "";
  const stem = cdnStem(u);
  const base = store.get(stem);
  if (base === undefined) { requestBynderMatch(u); return ""; }
  if (!base || !w || !h) return "";
  return base + "?io=transform:fill,width:" + Math.round(w) + ",height:" + Math.round(h);
}

/** test seam: an answer, as if Bynder had replied */
export function __setBynderMatch(u: unknown, transformBase: string | null): void { store.set(cdnStem(u), transformBase); bump(); }

export const bynderMatchVersion = (): number => version;
const subscribe = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };
/** re-render when any lookup lands — put the value in the fill effect's deps */
export function useBynderMatchVersion(): number {
  return useSyncExternalStore(subscribe, bynderMatchVersion, bynderMatchVersion);
}
